import { useState, createContext, useContext } from "react";
import "@/index.css";
import { useCurrentUser } from "@/hooks/useStore";
import type { User } from "@/hooks/useStore";
import { Header } from "@/components/Header";
import { Feed } from "@/components/Feed";
import { SubmitPage } from "@/components/SubmitPage";
import { RoastDetailPage } from "@/components/RoastDetailPage";
import { LeaderboardPage } from "@/components/LeaderboardPage";
import { ProfilePage } from "@/components/ProfilePage";
import { LoginDialog } from "@/components/LoginDialog";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

type Page =
	| { name: "feed" }
	| { name: "submit" }
	| { name: "roast"; id: string }
	| { name: "leaderboard" }
	| { name: "profile"; id: string };

interface AppContextType {
	currentUser: User | null;
	login: (username: string, bio?: string) => Promise<User>;
	logout: () => void;
	navigate: (page: Page) => void;
	showLogin: () => void;
}

export const AppContext = createContext<AppContextType>(null!);
export const useApp = () => useContext(AppContext);

export default function App() {
	const { user, login, logout } = useCurrentUser();
	const [page, setPage] = useState<Page>({ name: "feed" });
	const [loginOpen, setLoginOpen] = useState(false);

	const navigate = (p: Page) => setPage(p);
	const showLogin = () => setLoginOpen(true);

	return (
		<AppContext.Provider value={{ currentUser: user, login, logout, navigate, showLogin }}>
			<TooltipProvider>
				<div className="min-h-screen bg-background text-foreground">
					<Header currentPage={page.name} />
					<main className="mx-auto max-w-4xl px-4 py-6">
						{page.name === "feed" && <Feed />}
						{page.name === "submit" && <SubmitPage />}
						{page.name === "roast" && <RoastDetailPage roastId={page.id} />}
						{page.name === "leaderboard" && <LeaderboardPage />}
						{page.name === "profile" && <ProfilePage userId={page.id} />}
					</main>
					<LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
					<Toaster />
				</div>
			</TooltipProvider>
		</AppContext.Provider>
	);
}
